import {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  Fragment,
  useRef,
} from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { toast } from '@conexasaude/conexa-hero'

import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useAuthContext } from '../../contexts/AuthContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useScreeningData } from '@/modules/Screening/contexts/screeningContext'
import { useNavigation } from '@/hooks/useNavigation'
import { AccessRequestSentModal } from '../../components/AccessRequestSentModal'
import { MinorAccessRequestModal } from '../../components/MinorAccessRequestModal'
import { RenderIf } from '@/components/RenderIf'
import LoadingPage from '@/components/LoadingPage'

import services from '@/modules/Auth/auth-services'
import themeServices from '@/theme/theme.services'
import { setCache, getCache, resetCache } from '@/utils/cache'
import redirectToOldApplication from '@/utils/redirectToOldApplication'

import type {
  ClinicAndFlags,
  IUser,
  IUserData,
  MagicLinkByAccount,
} from '../../auth-types'
import {
  COMPOUND_SPECIALITIES,
  LOGGED_IN_WITH_MAGIC_LINK,
  REDIRECT_PATTERNS,
} from './consts'
import { MagicLinkParams } from './types'
import {
  useMagicLinkByAccount,
  useCheckMinorsTermAcceptance,
  useGetUsersProfilesFlowByAccount,
  useAuthMagicLink,
} from '../../queries'
import { useMinorAccessRequestStore } from '../../store/MinorAccessRequest.store'
import { usePatientRegistrationStore } from '@/modules/Signup/store/PatientRegistration.store'
import { ConexaGenericError } from '@/services/httpClient'
import { usePatientAccountStore } from '../../store/PatientAccount.store'
import { useCanRedirectToNewSchedule } from '@/modules/Attendance/submodules/Scheduled/queries'
import { parseMagicLinkUrl } from '@/utils/regExp'

const MagicLink: FunctionComponent = () => {
  const [state, setState] = useState({
    isLoading: true,
    parsedRedirect: null as string | null,
    enableMultiprofileRedirection: false,
  })
  const hasRunRef = useRef(false)
  const location = useLocation()
  const { token, redirectTo } = useParams<MagicLinkParams>()
  const { embed } = useThemeGeneratorContext()

  const { signIn, choiceUser, setUserClinic, signOut } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { showModal } = useModalContext()
  const navigate = useNavigation()
  const { setScreeningData } = useScreeningData()
  const { setUserData } = usePatientRegistrationStore()
  const { setMinorAccessRequestData } = useMinorAccessRequestStore()
  const { setAccount } = usePatientAccountStore()

  const { mutateAsync: checkMinorsTermAcceptance } =
    useCheckMinorsTermAcceptance()
  const { mutateAsync: magicLinkByAccount } = useMagicLinkByAccount()
  const { mutateAsync: getUsersProfilesFlowByAccount } =
    useGetUsersProfilesFlowByAccount()
  const { mutateAsync: authMagicLink } = useAuthMagicLink()
  const { mutateAsync: checkRedirectToNewSchedule } =
    useCanRedirectToNewSchedule()
  const { user } = useAuthContext()
  const currentPath = location.pathname

  const setDataModalMinorAccessRequest = useCallback(
    (userData: IUserData) => {
      setMinorAccessRequestData({
        idClinic: userData?.user?.clinic.id,
        idPatient: userData?.user?.idPatient,
        cpf: userData?.user?.patient?.cpf,
        holder: userData?.user?.patient?.holder,
        clinicProduct: userData?.user?.clinic?.product,
        patientName: userData?.user?.patient?.name,
      })
    },
    [setMinorAccessRequestData],
  )

  const handleError = useCallback(
    (error: ConexaGenericError) => {
      const errorMessage =
        error?.response?.data?.msg ||
        error?.response?.data?.object?.msg ||
        error?.message ||
        'Ocorreu um erro inesperado'

      if (!errorMessage.includes('autenticação')) {
        toast({
          message: errorMessage,
          variant: 'error',
        })
      }

      setCache('link-to-redirect-after-login', redirectTo)

      if (error?.toString()?.includes('403')) {
        navigate('/cadastro/cpf')
      } else {
        navigate('/')
      }
    },
    [navigate, redirectTo],
  )

  const checkMultiprofileRedirection = useCallback((users: IUser[]) => {
    const redirectToMultiprofile = users?.find(
      user =>
        user?.usuario?.paciente?.titularidade === 'TITULAR' && user?.frontCnx,
    )
    setState(prev => ({
      ...prev,
      enableMultiprofileRedirection: !!redirectToMultiprofile,
    }))
    return !!redirectToMultiprofile
  }, [])

  const directLogin = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }))

    try {
      const userData = await services.getUserData()
      setDataModalMinorAccessRequest(userData)

      if (userData?.user?.blocked) {
        signOut(true)
        navigate('/login')
        return
      }

      const userToken = getCache<string>('token')
      let users = await services.magicLinkLogin(token)

      const userClinic: ClinicAndFlags = await services.getClinic(userToken)
      if (!userClinic?.flags?.NovaExperiencia) {
        redirectToOldApplication()
        return
      }

      if (userClinic?.flags?.PossuiAplicativoProprio) {
        users = users.filter(
          user => user.usuario.clinica.id === userClinic.clinic.idClinica,
        )
      }

      const clinicColors = await themeServices.getClinicColorsById(
        userClinic.clinic.idClinica,
      )

      const ip = (await services.getUserIP()) || ''
      const termsAcceptance = await services.checkTermsAcceptance()
      const selectedUser = users?.find(
        u => u?.usuario?.id === userData?.user?.id,
      )

      signIn(users)
      choiceUser(userData?.user?.id, ip)

      if (!termsAcceptance) return

      setThemeColors(clinicColors)
      setUserClinic(userClinic)
      setSpecialities(userClinic?.clinic)
      setCache(LOGGED_IN_WITH_MAGIC_LINK, new Date().getTime())

      if (!selectedUser?.usuario?.flagAcessou) {
        await services.validateFirstAccess(userToken)
      }

      return checkMultiprofileRedirection(users)
    } catch (error) {
      handleError(error)
    }
  }, [
    choiceUser,
    navigate,
    setDataModalMinorAccessRequest,
    setSpecialities,
    setThemeColors,
    setUserClinic,
    signIn,
    signOut,
    token,
    checkMultiprofileRedirection,
    handleError,
  ])

  const resolveSpecialityLength = useCallback(
    (url: string) => {
      let specialityId = ''
      if (url.split('/').length === 2) specialityId = url.split('/')[1]
      else specialityId = url.split('/')[1] + '_' + url.split('/')[2]

      setScreeningData(screening => {
        screening.professionalType = specialityId
        return screening
      })
    },
    [setScreeningData],
  )

  const redirectToEmbed = useCallback(
    (url: string) => {
      let parsedUrl = `/${url}`

      if (embed.full) {
        parsedUrl = `${parsedUrl}?embedFull=true`
      }

      setState(prev => ({ ...prev, parsedRedirect: parsedUrl }))
      navigate(parsedUrl)
    },
    [embed.full, navigate],
  )

  const formatRedirectUrl = useCallback(
    (redirectTo: string) => {
      let formattedUrl = redirectTo?.replace(/_/g, '/')

      COMPOUND_SPECIALITIES.forEach(speciality => {
        const specialityWithSlash = speciality?.replace('_', '/')
        formattedUrl = formattedUrl?.replace(specialityWithSlash, speciality)
      })

      if (location.search) {
        formattedUrl = `${formattedUrl}${location.search}`
      }
      return formattedUrl
    },
    [location.search],
  )

  const handleSpecialRedirects = useCallback(
    async (formattedUrl: string, termsAcceptance: boolean) => {
      if (formattedUrl === 'home/atendimento/imediato') {
        setCache('scheduledHidden', true)
        setState(prev => ({ ...prev, parsedRedirect: '/' }))
        if (termsAcceptance) {
          redirectToEmbed('')
        }
        return true
      }

      if (formattedUrl.includes('atendimento-imediato/')) {
        resolveSpecialityLength(formattedUrl)
        setState(prev => ({ ...prev, parsedRedirect: 'triagem' }))
        if (termsAcceptance) {
          redirectToEmbed('triagem')
        }
        return true
      }

      if (formattedUrl.includes('especialidades')) {
        const professionalType = formattedUrl.split('/')[1].toLowerCase()
        setState(prev => ({
          ...prev,
          parsedRedirect: `atendimento-agendado/preferencia/${professionalType}/especialidades`,
        }))

        if (termsAcceptance) {
          redirectToEmbed(
            `atendimento-agendado/preferencia/${professionalType}/especialidades`,
          )
        }
        return true
      }

      if (
        formattedUrl.includes(
          'atendimento-agendado-psicologo/busca-profissional/perfil-profissional',
        )
      ) {
        setState(prev => ({ ...prev, parsedRedirect: formattedUrl }))

        if (termsAcceptance) {
          const newFullProfileUrl = formattedUrl.replace(
            'busca-profissional/perfil-profissional',
            'perfil',
          )
          redirectToEmbed(newFullProfileUrl)
        }

        return true
      }

      if (
        formattedUrl.includes('atendimento-agendado') &&
        !formattedUrl.includes('preferencia') &&
        !formattedUrl.includes('categoria') &&
        !formattedUrl.includes('busca-profissional/v2')
      ) {
        const isQueueSlot = formattedUrl.includes('encaixe')
        setState(prev => ({ ...prev, parsedRedirect: `/${formattedUrl}` }))

        if (isQueueSlot && termsAcceptance) {
          redirectToEmbed(formattedUrl)
        }

        if (termsAcceptance && !isQueueSlot) {
          redirectToEmbed('atendimento-agendado/validar/atendimento')
        }

        return true
      }

      if (formattedUrl.includes('meus-atendimentos')) {
        const id = formattedUrl.split('/')[2]
        const action = formattedUrl.split('/')[3] || ''

        if (termsAcceptance) {
          if (!id) {
            redirectToEmbed('meus-atendimentos')
          } else if (id.length < 30) {
            redirectToEmbed(`meus-atendimentos/atendimento/${id}/${action}`)
          } else {
            redirectToEmbed(
              `meus-atendimentos/atendimento/prefila/${id}/${action}`,
            )
          }
        }
        return true
      }

      if (formattedUrl.includes('minhas-reservas')) {
        const id = formattedUrl.split('/')[2]
        redirectToEmbed(`minhas-reservas/confirmar-reserva/${id}`)
        return true
      }

      if (formattedUrl.includes('signup-multiprofile')) {
        redirectToEmbed('escolha')
        return true
      }
      if (formattedUrl.includes('mapeamento-psicossocial')) {
        setState(prev => ({ ...prev, parsedRedirect: `${formattedUrl}` }))
        if (termsAcceptance) {
          redirectToEmbed('mapeamento-psicossocial')
        }
        return true
      }

      return false
    },
    [redirectToEmbed, resolveSpecialityLength],
  )

  const flowByAccountAuthUser = useCallback(
    async (token: string) => {
      try {
        const users = await getUsersProfilesFlowByAccount(token)
        signIn(users)
        setState(prev => ({ ...prev, isLoading: false }))
        navigate('/conta/escolha')
      } catch (error) {
        handleError(error)
      }
    },
    [getUsersProfilesFlowByAccount, handleError, navigate, signIn],
  )

  const handleFlowByAccount = useCallback(
    (userData: MagicLinkByAccount) => {
      setAccount({
        accountToken: userData?.token ?? null,
        idAccount: userData?.idAccount ?? null,
      })

      if (!userData?.hasPatient) {
        setUserData({
          name: userData?.name ?? '',
          email: userData?.email ?? '',
          cpf: userData?.cpf ?? '',
          birthdate: userData?.birthDate,
          hasPatient: userData?.hasPatient,
          cellphone: userData?.cellphone ?? '',
        })
        setState(prev => ({ ...prev, isLoading: false }))
        navigate('/cadastro/conta')
      } else flowByAccountAuthUser(userData?.token)
    },
    [flowByAccountAuthUser, navigate, setAccount, setUserData],
  )

  const handleConfirmEmailPath = useCallback(() => {
    const userToken = currentPath?.split('/navegador/')?.[1]?.split('/')?.[0]
    const attendanceToken = currentPath?.split('/confirmar-via-email_')?.[1]

    setState(prev => ({ ...prev, isLoading: false }))
    navigate(`/confirmando-sua-consulta/${userToken}/${attendanceToken}`)
  }, [currentPath, navigate])

  const checkTokenValidity = useCallback(async () => {
    const linkToRedirectAfterLogin = getCache('link-to-redirect-after-login')
    resetCache()
    setCache('link-to-redirect-after-login', linkToRedirectAfterLogin)

    try {
      setState(prev => ({ ...prev, isLoading: true }))

      if (currentPath?.includes('confirmar-via-email')) {
        handleConfirmEmailPath()
        return
      }

      if (currentPath?.includes('conta')) {
        const userData = await magicLinkByAccount(token)
        handleFlowByAccount(userData)
        return userData
      }

      if (!currentPath?.includes('multilogin')) {
        const userToken = await authMagicLink(token)
        setCache('token', userToken)

        const isMinorTermAccepted = await checkMinorsTermAcceptance(userToken)
        const termsAcceptance = await services.checkTermsAcceptance()
        const hasMultiprofile = await directLogin()

        if (termsAcceptance && hasMultiprofile) {
          navigate('/escolha')
          return
        }

        if (redirectTo) {
          const formattedUrl = formatRedirectUrl(redirectTo)
          const handled = await handleSpecialRedirects(
            formattedUrl,
            termsAcceptance,
          )

          if (!handled) {
            if (termsAcceptance) {
              setState(prev => ({
                ...prev,
                parsedRedirect: `/${formattedUrl}`,
              }))
            }
          }
        } else if (termsAcceptance) {
          setState(prev => ({ ...prev, parsedRedirect: '/' }))
          navigate('/')
        }

        if (!isMinorTermAccepted) {
          showModal('GUARDIAN_ACCESS_REQUEST_MODAL', { persistent: true })
          return
        }

        if (!termsAcceptance) {
          showModal('TERMS_AND_CONDITIONS', {
            title: 'Termos de uso',
            width: 455,
            persistent: true,
          })
          return
        }
      } else {
        const users = await services.magicLinkLoginMultiprofile(token)
        signIn(users)
        setState(prev => ({ ...prev, isLoading: false }))
        navigate('/escolha-unificada')
      }
    } catch (error) {
      handleError(error)
    }
  }, [
    currentPath,
    handleConfirmEmailPath,
    magicLinkByAccount,
    token,
    handleFlowByAccount,
    authMagicLink,
    checkMinorsTermAcceptance,
    directLogin,
    redirectTo,
    navigate,
    formatRedirectUrl,
    handleSpecialRedirects,
    showModal,
    signIn,
    handleError,
  ])

  const buildNewScheduleUrl = useCallback(
    async (parsedRedirect: string) => {
      let formattedUrl = parsedRedirect
      const { council, professionalId, specialtyId } =
        parseMagicLinkUrl(formattedUrl)

      if (user?.token && (council || professionalId)) {
        const canRedirectToNewSchedule = await checkRedirectToNewSchedule({
          council,
          ...(professionalId && { professionalId }),
          ...(specialtyId && { specialtyId }),
          originUrl: window.location.href,
        })

        if (
          council === 'CRP' &&
          canRedirectToNewSchedule &&
          professionalId &&
          parsedRedirect?.includes('atendimento-agendado/categoria')
        ) {
          formattedUrl = formattedUrl.replace(
            /atendimento-agendado\/categoria\/PSICOLOGO/,
            'atendimento-agendado-psicologo/perfil',
          )
        }

        if (canRedirectToNewSchedule) {
          const matchedPattern = REDIRECT_PATTERNS.find(pattern =>
            parsedRedirect?.startsWith(pattern),
          )

          if (matchedPattern) {
            formattedUrl = formattedUrl.replace(
              matchedPattern,
              'atendimento-agendado/busca-profissional-agenda/',
            )
          }
        }
      }
      return formattedUrl
    },
    [user?.token, checkRedirectToNewSchedule],
  )

  useEffect(() => {
    if (user?.token) {
      const processRedirectUrl = async () => {
        setState(prev => ({ ...prev, isLoading: false }))

        if (state.parsedRedirect) {
          const urlRedirect = buildNewScheduleUrl(state.parsedRedirect)
          navigate(await urlRedirect)
        }
      }
      processRedirectUrl()
    }
  }, [user?.token, buildNewScheduleUrl, navigate, state.parsedRedirect])

  useEffect(() => {
    if (!hasRunRef.current) {
      hasRunRef.current = true
      checkTokenValidity()
    }
  }, [checkTokenValidity])

  return (
    <Fragment>
      <RenderIf condition={state.isLoading}>
        <LoadingPage
          withoutNav
          text="Estamos deixando tudo pronto pra você!"
          useNeutralColor
        />
      </RenderIf>
      <TermsAndConditionsModal
        redirectTo={state.parsedRedirect}
        directLogin={directLogin}
        setLoading={loading =>
          setState(prev => ({ ...prev, isLoading: loading }))
        }
        userToken={getCache<string>('token')}
      />
      <MinorAccessRequestModal />
      <AccessRequestSentModal />
    </Fragment>
  )
}

export default MagicLink
